import { findFieldByServicePath } from "@tc/field-anchor"
import { productPaginationActions } from "../config/useProductPagination"
import type { CoreLookupInput } from "@tc/ui-dfe/query"
import { FormSearchProps, SelectType } from "@tc/ui-dfe/utils/types"
import { TypesenseContext, useTypesenseClient } from "@tc/ui-typesense-client"
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { debounce } from "@mui/material/utils"
import {
  ControllerRenderProps,
  FieldValues,
  useFormContext,
} from "react-hook-form"
import { useInfiniteHits, useSearchBox } from "react-instantsearch"
import { useRecipe } from "../useRecipe/useRecipe"
import { CollectionName as collectionNameMap } from "@tc/search-document-builders/collection-names"

interface HookOptions<
  TFieldValues extends FieldValues,
  TDoc extends Record<string, unknown>,
> {
  field: ControllerRenderProps<
    TFieldValues,
    FormSearchProps<TFieldValues, TDoc>["name"]
  >
  props: FormSearchProps<TFieldValues, TDoc>
  searchBoxRefineRef?: React.MutableRefObject<((query: string) => void) | null>
}

// Type guard for CoreLookupInput
const isCoreLookupInput = (item: unknown): item is CoreLookupInput => {
  return (
    typeof item === "object" && item !== null && "id" in item && "name" in item
  )
}

export const useFormSearchData = <
  TFieldValues extends FieldValues,
  TDoc extends Record<string, unknown>,
>({
  field,
  props,
  searchBoxRefineRef,
}: HookOptions<TFieldValues, TDoc>) => {
  const {
    valueAttribute = "id",
    displayAttr = "name",
    customOption,
    multiple,
    onHitChange,
    defaultValue,
    clientFilter,
    freeSolo,
    collectionName,
    filter_by,
    allowBulkEntry,
    includeNotApplicable,
    dependencyFilterKey,
  } = props

  const { hits, showMore, isLastPage } = useInfiniteHits<TDoc>()
  const { refine } = useSearchBox()
  const debouncedRefine = useMemo(() => debounce(refine, 300), [refine])
  const {
    formState: { isSubmitting },
  } = useFormContext()

  // Store refine function in ref for external access
  useEffect(() => {
    if (searchBoxRefineRef) {
      searchBoxRefineRef.current = refine
    }
  }, [refine, searchBoxRefineRef])

  const [selectedValue, setSelectedValue] = useState<SelectType[]>([])
  const [inputValue, setInputValue] = useState<string>("")
  const [isInitialized, setIsInitialized] = useState(false)
  const [fetchedDocs, setFetchedDocs] = useState<TDoc[]>([])

  // Clear invalid input immediately when save draft
  useEffect(() => {
    if (isSubmitting && selectedValue.length === 0) {
      setInputValue("")
      refine("*")
    }
  }, [isSubmitting, selectedValue.length, refine])

  const selectedItemsRef = useRef<Map<string, TDoc>>(new Map())
  const isClearingRef = useRef(false)

  const recipe = useRecipe()
  const submissionLayout = recipe?.recipe?.meta?.submissionLayout as
    | "AP_EXPORT"
    | "AP_TRANSFER"
    | "PHYTO_EXPORT"

  const { apiKey } = useTypesenseClient({
    context:
      submissionLayout === "AP_TRANSFER"
        ? "transfers"
        : submissionLayout === "AP_EXPORT" ||
            submissionLayout === "PHYTO_EXPORT"
          ? "exports"
          : "generic",
    searchParams: { query_by: "*" },
    collectionId: props.collectionId,
    collectionName,
    usePermission: submissionLayout
      ? productPaginationActions.cachedFieldLookup(
          field.name,
          findFieldByServicePath,
        )?.searchRepresentation?.[submissionLayout]?.usePermissions
      : undefined,
  })

  const { typesenseHost } = useContext(TypesenseContext) || {}

  // Bulk validation: queries Typesense on-demand with only the entered codes
  const [isBulkValidating, setIsBulkValidating] = useState(false)
  const skipNextFetchRef = useRef(false) // Skip fetchDocumentsByIds after bulk entry

  const bulkValidateRef = useRef<
    | ((codes: string[]) => Promise<{
        resolved: { value: string; label: string }[]
        invalid: string[]
      }>)
    | null
  >(null)

  useEffect(() => {
    if (!allowBulkEntry || !apiKey || !typesenseHost || !collectionName) {
      bulkValidateRef.current = null
      return
    }

    bulkValidateRef.current = async (codes: string[]) => {
      // Sanitize valueAttribute like the existing fetchByIds logic
      const sanitizedValueAttribute =
        typeof valueAttribute === "string"
          ? valueAttribute.replaceAll(/[^a-zA-Z0-9_-]/g, "")
          : ""

      if (!sanitizedValueAttribute) {
        throw new Error("Invalid valueAttribute for bulk validation")
      }

      // Build multi_search request - reuses existing DFE pattern
      // Single search with filter_by to get all matching codes at once
      const filterValue = codes.map((c) => c.toUpperCase()).join(", ")
      const searches = [
        {
          collection: collectionName,
          q: "*",
          filter_by: `${sanitizedValueAttribute}:=[${filterValue}] && active:=true`,
          per_page: codes.length,
        },
      ]

      const url = `${typesenseHost}/search/multi_search`

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "X-Typesense-Api-Key": apiKey,
          "Content-Type": "text/plain",
        },
        body: JSON.stringify({ searches }),
      })

      if (!response.ok) {
        throw new Error(`Typesense returned ${response.status}`)
      }

      const data = await response.json()

      // Build a map of found codes from the search result
      const foundMap = new Map<string, { value: string; label: string }>()
      const hits = data?.results?.[0]?.hits || []
      hits.forEach((hit: { document: Record<string, string> }) => {
        const doc = hit.document
        const code = doc[sanitizedValueAttribute] || ""
        const name = doc["label"] || doc["item_name"] || code
        // When displayAttr contains comma (e.g., "alpha_2_code,name"), show "CODE - Name" format
        const displayLabel =
          typeof displayAttr === "string" && displayAttr.includes(",")
            ? `${code} - ${name}`
            : name
        foundMap.set(code.toUpperCase(), { value: code, label: displayLabel })
      })

      // Check each input code against found results
      const resolved: { value: string; label: string }[] = []
      const invalid: string[] = []

      for (const code of codes) {
        const match = foundMap.get(code.toUpperCase())
        if (match) {
          resolved.push(match)
        } else {
          invalid.push(code)
        }
      }

      return { resolved, invalid }
    }
  }, [
    allowBulkEntry,
    apiKey,
    typesenseHost,
    collectionName,
    valueAttribute,
    displayAttr,
  ])

  // Helper to extract ID from value (handles both string and CoreLookupInput format)
  const extractId = useCallback((value: unknown): string | null => {
    if (!value) return null
    if (typeof value === "string") return value
    if (isCoreLookupInput(value)) {
      return value.id || null
    }
    // Check if it's an object with __typename (GraphQL response)
    if (typeof value === "object" && value !== null && "id" in value) {
      const id = (value as { id: unknown }).id
      return typeof id === "string" ? id : null
    }
    return null
  }, [])

  const hasPrunedRef = useRef(false)
  const fieldRef = useRef(field)
  fieldRef.current = field
  const multipleRef = useRef(multiple)
  multipleRef.current = multiple
  const freeSoloRef = useRef(freeSolo)
  freeSoloRef.current = freeSolo

  // Fetch specific documents by ID
  const fetchDocumentsByIds = useCallback(
    async (ids: string[]) => {
      if (!apiKey || !typesenseHost || ids.length === 0) return

      try {
        const sanitizedIds = ids
          .filter((id) => typeof id === "string" && id.trim().length > 0)
          .map((id) => {
            const cleaned = id.replaceAll(/[^a-zA-Z0-9_-]/g, "")
            return cleaned.length > 0 && cleaned.length <= 100 ? cleaned : null
          })
          .filter((id): id is string => id !== null)

        if (sanitizedIds.length === 0) return

        const sanitizedValueAttribute =
          typeof valueAttribute === "string"
            ? valueAttribute.replaceAll(/[^a-zA-Z0-9_-]/g, "")
            : ""

        if (!sanitizedValueAttribute) {
          console.error("Invalid valueAttribute provided")
          return
        }

        const valueFilter = `${sanitizedValueAttribute}:=[${sanitizedIds.join(",")}]`
        const combinedFilter = filter_by
          ? `${filter_by} && ${valueFilter}`
          : valueFilter

        const searchParams = new URLSearchParams({
          q: "*",
          filter_by: combinedFilter,
          per_page: Math.min(sanitizedIds.length, 250).toString(),
        })

        const response = await fetch(
          `${typesenseHost}/search/collections/${collectionName}/documents/search?${searchParams}`,
          {
            headers: { "X-Typesense-Api-Key": apiKey },
          },
        )

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        }

        const data = await response.json()
        const documents =
          data?.hits?.map((hit: { document: TDoc }) => hit.document) || []
        setFetchedDocs(documents as TDoc[])

        // Prune unresolved entries inline — handles inactive operators pre-filled from parent SD
        if (
          !hasPrunedRef.current &&
          multipleRef.current &&
          !freeSoloRef.current &&
          filter_by
        ) {
          const resolvedIds = new Set(
            documents.map((doc: TDoc) => doc[valueAttribute] as string),
          )
          const currentValues = Array.isArray(fieldRef.current.value)
            ? (fieldRef.current.value as unknown[])
            : []
          const validValues = currentValues.filter((item) => {
            const id = extractId(item)
            return id !== null && resolvedIds.has(id)
          })
          if (validValues.length < currentValues.length) {
            hasPrunedRef.current = true
            fieldRef.current.onChange(validValues)
          }
        }
      } catch (error) {
        console.error("Failed to fetch documents:", error)
        setFetchedDocs([])
      }
    },
    [
      apiKey,
      typesenseHost,
      collectionName,
      filter_by,
      valueAttribute,
      extractId,
    ],
  )

  // Initialize search and fetch specific documents
  const fetchDocsByIdsRef = useRef(fetchDocumentsByIds)
  fetchDocsByIdsRef.current = fetchDocumentsByIds
  const isInitializedRef = useRef(false)

  useEffect(() => {
    if (isInitializedRef.current) return
    isInitializedRef.current = true

    if (field?.value) {
      const values = Array.isArray(field.value) ? field.value : [field.value]
      const ids = values
        .map(extractId)
        .filter((id): id is string => id !== null)

      if (ids.length > 0) {
        fetchDocsByIdsRef.current(ids)
      }
    }
    setIsInitialized(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Fetch documents whenever field value changes (after initialization)
  const hasInitialFetchRef = useRef(false)
  useEffect(() => {
    if (!isInitialized || !field?.value) return

    if (!hasInitialFetchRef.current) {
      hasInitialFetchRef.current = true
      return
    }

    // Skip fetch if value was just updated by bulk entry (we already have the data)
    if (skipNextFetchRef.current) {
      skipNextFetchRef.current = false
      return
    }

    const values = Array.isArray(field.value) ? field.value : [field.value]
    const ids = values.map(extractId).filter((id): id is string => id !== null)

    if (ids.length > 0) {
      fetchDocumentsByIds(ids)
    }
  }, [field?.value, isInitialized, extractId, fetchDocumentsByIds])

  // Helper function to get display value based on displayAttr
  const getDisplayValue = useCallback(
    (hit: TDoc) => {
      if (customOption) return customOption(hit)

      if (typeof displayAttr === "string" && displayAttr.includes(",")) {
        const [first, second] = displayAttr.split(",")
        return `${hit[first as keyof TDoc]} - ${hit[second as keyof TDoc]}`
      }
      return hit[displayAttr] as string
    },
    [customOption, displayAttr],
  )

  // Helper function to get form value based on displayAttr
  const getFormValue = useCallback(
    (hit: TDoc) => {
      return hit[valueAttribute] as string
    },
    [valueAttribute],
  )

  // Determine if NOT_APPLICABLE should be excluded based on whether specific items exist
  // for the selected parent. Used to filter NOT_APPLICABLE from dropdown options.
  const shouldExcludeNotApplicable = useMemo(() => {
    if (!includeNotApplicable || !dependencyFilterKey) return false
    const filtered = clientFilter ? hits.filter(clientFilter) : hits
    if (filtered.length === 0) return false
    return filtered.some((hit) => {
      const parentCode = hit[dependencyFilterKey as keyof TDoc]
      return parentCode !== "NOT_APPLICABLE"
    })
  }, [hits, clientFilter, includeNotApplicable, dependencyFilterKey])

  // Create options from hits and fetched documents
  const options = useMemo(() => {
    let filtered = clientFilter ? hits.filter(clientFilter) : hits

    // When includeNotApplicable is true, filter out NOT_APPLICABLE items
    // if there are other specific items for the selected parent.
    // This ensures "Not Applicable" only shows when there are no specific children.
    if (shouldExcludeNotApplicable) {
      // Remove NOT_APPLICABLE items since specific items exist
      filtered = filtered.filter((hit) => {
        const parentCode = hit[dependencyFilterKey as keyof TDoc]
        return parentCode !== "NOT_APPLICABLE"
      })
    }

    // Create map of existing hits by their form value
    const hitsMap = new Map(filtered.map((hit) => [getFormValue(hit), hit]))

    // Separate fetched docs (selected values) from regular hits
    const selectedDocs = fetchedDocs.filter((doc) =>
      hitsMap.has(getFormValue(doc)),
    )
    let additionalDocs = fetchedDocs.filter(
      (doc) => !hitsMap.has(getFormValue(doc)),
    )

    // When dependencyFilterKey is set (field depends on a parent), don't show
    // additionalDocs if there are no hits. This prevents stale values from
    // appearing when the parent is cleared or not yet selected.
    if (dependencyFilterKey && filtered.length === 0) {
      additionalDocs = []
    }

    // Filter out NOT_APPLICABLE from additionalDocs when specific items exist.
    if (shouldExcludeNotApplicable && dependencyFilterKey) {
      additionalDocs = additionalDocs.filter((doc) => {
        const parentCode = doc[dependencyFilterKey as keyof TDoc]
        return parentCode !== "NOT_APPLICABLE"
      })
    }

    // Remove selected docs from filtered hits to avoid duplicates
    const unselectedHits = filtered.filter((hit) => {
      const formValue = getFormValue(hit)
      return !fetchedDocs.some((doc) => getFormValue(doc) === formValue)
    })

    // Final list: selected docs first, then unselected hits, then additional fetched docs
    const finalHits = [...selectedDocs, ...unselectedHits, ...additionalDocs]

    const availableOptions: SelectType[] = finalHits.map((hit) => ({
      value: getFormValue(hit),
      label: getDisplayValue(hit),
      originHit: hit,
    }))

    if (!isLastPage) {
      availableOptions.push({ label: "show more", value: undefined })
    }
    return availableOptions
  }, [
    hits,
    clientFilter,
    getFormValue,
    getDisplayValue,
    isLastPage,
    fetchedDocs,
    shouldExcludeNotApplicable,
    dependencyFilterKey,
  ])

  // Sync UI with form value
  useEffect(() => {
    if (isClearingRef.current) {
      return
    }

    if (!field.value) {
      setSelectedValue([])
      selectedItemsRef.current.clear()
      return
    }

    // Handle both CoreLookupInput objects and simple strings
    const fieldValues = (
      Array.isArray(field.value) ? field.value : [field.value]
    ) as unknown[]
    const formValues = new Set(
      fieldValues.map(extractId).filter((id): id is string => id !== null),
    )

    // Clear and rebuild selectedItemsRef to match current form values
    selectedItemsRef.current.clear()

    const selectedOptions = options
      .filter((option) => option.value && formValues.has(option.value))
      .map((option) => {
        if (option.originHit && option.value) {
          selectedItemsRef.current.set(option.value, option.originHit as TDoc)
        }
        return option
      })

    // Handle freeSolo: create custom options for values not found in search results
    if (freeSolo) {
      const resolvedValues = new Set(selectedOptions.map((opt) => opt.value))
      const coreLookupItems = fieldValues.filter(isCoreLookupInput)

      // Recover custom entries: id is null, or id didn't resolve from Typesense
      const customOptions = coreLookupItems
        .filter(
          (item) =>
            item.name &&
            (item.id === null || !resolvedValues.has(item.id ?? undefined)),
        )
        .map((item) => ({
          value: item.name || "",
          label: item.name || "",
        }))
      selectedOptions.push(...customOptions)

      // Also handle plain string values that aren't in options
      const stringValues = fieldValues.filter(
        (val): val is string => typeof val === "string",
      )
      const missingStringValues = stringValues.filter(
        (val) => !resolvedValues.has(val),
      )
      selectedOptions.push(
        ...missingStringValues.map((val) => ({ value: val, label: val })),
      )
    }

    // Fallback for non-freeSolo: display stored name when Typesense can't resolve
    if (!freeSolo) {
      const resolvedValues = new Set(selectedOptions.map((opt) => opt.value))
      const coreLookupItems = fieldValues.filter(isCoreLookupInput)
      const unresolvedOptions = coreLookupItems
        .filter((item) => item.name && item.id && !resolvedValues.has(item.id))
        .map((item) => ({
          value: item.id ?? item.name ?? "",
          label:
            item.id &&
            item.name &&
            item.id !== item.name &&
            !item.name.startsWith(item.id)
              ? `${item.id} - ${item.name}`
              : item.name || "",
          originHit: {
            [valueAttribute]: item.id,
            organisation_name: item.name,
            id: (item as Record<string, unknown>).registrationId ?? item.id,
          },
        }))
      selectedOptions.push(...unresolvedOptions)
    }

    setSelectedValue(multiple ? selectedOptions : selectedOptions.slice(0, 1))
  }, [field.value, options, multiple, freeSolo, extractId])

  const inputValueRef = useRef(inputValue)
  inputValueRef.current = inputValue

  /**
   * Fetches a single registration document by its valueAttribute ID from Typesense.
   * Used as a fallback when originHit is missing (race condition: user selects before
   * Typesense results arrive). Returns null if the fetch fails or fields are unavailable.
   */
  const fetchRegistrationById = useCallback(
    async (id: string): Promise<Record<string, string> | null> => {
      if (!apiKey || !typesenseHost || !collectionName) return null
      try {
        const sanitizedId = id.replaceAll(/[^a-zA-Z0-9_-]/g, "")
        if (!sanitizedId) return null

        const sanitizedValueAttr =
          typeof valueAttribute === "string"
            ? valueAttribute.replaceAll(/[^a-zA-Z0-9_-]/g, "")
            : ""
        if (!sanitizedValueAttr) return null

        // Include field-level filter_by so the re-fetch respects the same scope
        // as the normal Typesense search.
        const valueFilter = `${sanitizedValueAttr}:=[${sanitizedId}]`
        const combinedFilter = filter_by
          ? `${filter_by} && ${valueFilter}`
          : valueFilter

        const searchParams = new URLSearchParams({
          q: "*",
          filter_by: combinedFilter,
          per_page: "1",
        })

        const response = await fetch(
          `${typesenseHost}/search/collections/${collectionName}/documents/search?${searchParams}`,
          { headers: { "X-Typesense-Api-Key": apiKey } },
        )
        if (!response.ok) return null

        const data = await response.json()
        const doc = data?.hits?.[0]?.document
        return doc ?? null
      } catch {
        return null
      }
    },
    [apiKey, typesenseHost, collectionName, valueAttribute, filter_by],
  )

  const handleSelectionChange = useCallback(
    async (
      _event: React.SyntheticEvent,
      value: SelectType | SelectType[] | null,
    ) => {
      let selection: SelectType[] = []
      if (Array.isArray(value)) {
        selection = value
      } else if (value) {
        selection = [value]
      }
      const validSelections = selection.filter(
        (item) => item.value !== undefined,
      )
      const isRegistration =
        collectionName?.includes(collectionNameMap.Registrations) ?? false

      // For registration fields, re-fetch the doc by ID if originHit is missing.
      const enrichedSelections: SelectType[] = await Promise.all(
        validSelections.map(async (item) => {
          if (isRegistration && !item.originHit && item.value) {
            const fetched = await fetchRegistrationById(item.value)
            if (fetched) {
              return { ...item, originHit: fetched as unknown as TDoc }
            }
          }
          return item
        }),
      )

      const lookupValues: CoreLookupInput[] = enrichedSelections.map((item) => {
        const hit = item.originHit as Record<string, string> | undefined
        const isCustomFreeSolo = freeSolo && !hit
        // For name: use the raw attribute value from the hit, not the formatted display label.
        // When displayAttr is "item_code,item_name", item.label = "code - name" (display only).
        // The stored name should be just the text portion, not the combined label.
        let rawName = item.label
        if (
          hit &&
          typeof displayAttr === "string" &&
          displayAttr.includes(",")
        ) {
          // displayAttr is "first,second" — use the second attribute (typically the name/description)
          const [, nameAttr] = displayAttr.split(",").map((s) => s.trim())
          rawName = hit[nameAttr] ?? item.label
        } else if (hit && displayAttr) {
          // When "Columns to Display" is set to CODE in TEO, displayAttr resolves
          // to "item_code" (same as valueAttribute). If we use displayAttr here,
          // both id and name end up as the code (e.g. {id:"NZ", name:"NZ"}).
          // Use item_name instead to preserve the human-readable text.
          // For non-item collections (unit, country, agency, process), item_name
          // won't exist so it falls back to item.label (the correct display value).
          rawName = hit["item_name"] ?? item.label
        }
        return {
          id: isCustomFreeSolo ? null : item.value || null,
          // Fall back to item.label if re-fetch failed, to avoid storing an empty name.
          name:
            isRegistration && hit
              ? hit.organisation_name || ""
              : isRegistration
                ? item.label || ""
                : rawName,
          ...(isRegistration && hit ? { registrationId: hit.id } : {}),
        }
      })
      setSelectedValue(enrichedSelections)
      fieldRef.current.onChange(
        multiple ? lookupValues : (lookupValues[0] ?? defaultValue),
      )
      onHitChange?.(enrichedSelections.map((item) => item.originHit as TDoc))
      setInputValue("")
      multiple && refine(inputValueRef.current || "*")
    },
    [
      multiple,
      defaultValue,
      onHitChange,
      collectionName,
      freeSolo,
      refine,
      displayAttr,
      fetchRegistrationById,
    ],
  )

  const handleInputChange = useCallback(
    (_event: React.SyntheticEvent, newInputValue: string, reason: string) => {
      if (reason === "input") {
        setInputValue(newInputValue)
        // In multiple mode, preserve existing selections so chips remain visible while typing
        if (!multiple) {
          setSelectedValue([])
          setFetchedDocs([])
          selectedItemsRef.current.clear()
          field.onChange(defaultValue ?? null)
        }
        debouncedRefine(newInputValue || "*")

        // Handle freeSolo: save custom input value directly to form
        if (freeSolo && newInputValue && !multiple) {
          const customSelection: SelectType = {
            value: newInputValue,
            label: newInputValue,
          }
          setSelectedValue([customSelection])

          const customLookupValue: CoreLookupInput = {
            id: null,
            name: newInputValue,
          }
          field.onChange(customLookupValue)
        } else if (freeSolo && !newInputValue && !multiple) {
          // Clear freeSolo field when input is empty
          isClearingRef.current = true
          setSelectedValue([])
          field.onChange(null)
        }
      } else if (reason === "clear") {
        isClearingRef.current = true
        setInputValue("")
        refine("*")
        setSelectedValue([])
        field.onChange(multiple ? [] : null)
      }
    },
    [refine, freeSolo, multiple, field, defaultValue],
  )

  return {
    isLoading: !isInitialized,
    options,
    selectedValue,
    inputValue,
    showMore,
    isBulkValidating,
    bulkValidateRef,
    setIsBulkValidating,
    skipNextFetchRef,
    handlers: {
      handleSelectionChange,
      handleInputChange,
    },
  }
}
